import { eventListeners } from '@popperjs/core';
import { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  Children,
  isValidElement,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { usePopper } from 'react-popper';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type DataTestId,
  type MaskingProps,
  type WithChildren,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';
import {
  _useAriaLabelingProps,
  _useSafeForwardProps as useSafeForwardProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { XmarkIcon } from '@dynatrace/strato-icons';

import { Actions } from './Actions.js';
import {
  FeatureHighlightCloseButtonCSS,
  FeatureHighlightPointerArrowCSS,
  FeatureHighlightScrollContainerCSS,
} from './FeatureHighlight.sty.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { useKeyboardFocusHandling } from '../hooks/useKeyboardFocusHandling.js';
import { Content } from '../shared-components/Content.js';
import { Title } from '../shared-components/Title.js';
import { Visual } from '../shared-components/Visual.js';
import { basePlacementsType, basePlacements } from '../types/placement.js';
import {
  observeOverlaySize,
  observeAnimationDone,
} from '../utils/popper-modifiers.js';

// #region Typings

/**
 * Accepted placement properties for FeatureHighlight
 * @public
 */
export type FeatureHighlightPlacement =
  | 'left-top'
  | 'left-middle'
  | 'left-bottom'
  | 'right-top'
  | 'right-middle'
  | 'right-bottom'
  | 'top-left'
  | 'top-middle'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-middle'
  | 'bottom-right';

/**
 * Interface for the FeatureHighlight component.
 * @public
 */
export interface FeatureHighlightProps
  extends WithChildren,
    AriaLabelingProps,
    DataTestId,
    MaskingProps {
  /**
   * Whether the FeatureHighlight is currently shown or not. Has to be controlled by the consumer.
   */
  open: boolean;
  /**
   * The element the popover is referring to (i.e. where the pointer is pointing to).
   */
  anchor: HTMLElement | null;
  /**
   * Placement of the FeatureHighlight.
   * @defaultValue 'right-bottom'
   */
  placement?: FeatureHighlightPlacement;
  /**
   * Callback fired when closing the FeatureHighlight.
   */
  onClose: () => void;
}

type FeatureHighlightSlots = {
  title: ReactElement | null;
  visual: ReactElement | null;
  content: ReactElement | null;
  actions: ReactElement | null;
};
// #endregion Typings

const getPreferredPlacement = (
  placement: FeatureHighlightPlacement,
): basePlacementsType => {
  const preferredPlacement = placement.split('-')[0] as basePlacementsType;
  // check if passed placement is actually a valid value, otherwise return the
  if (basePlacements.includes(preferredPlacement)) {
    return preferredPlacement;
  } else {
    return basePlacements[1];
  }
};

/**
 * FeatureHighlight component
 */
const FeatureHighlightRoot = forwardRef<HTMLElement, FeatureHighlightProps>(
  (props, forwardedRef) => {
    const {
      open,
      anchor,
      placement = 'right-bottom',
      onClose,
      children,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;
    const preferredPlacement = getPreferredPlacement(placement);
    const [overlayElement, setOverlayElement] = useState<HTMLElement | null>();
    const [arrowElement, setArrowElement] = useState<HTMLElement | null>();
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const featureHighlightContainer = useRef<HTMLElement | null>(null);
    const rootRef = useMergeRefs<HTMLElement | null>([
      setOverlayElement,
      forwardedRef,
    ]);
    const [handleFocusOnKeydown, closedOnKeydown] =
      useKeyboardFocusHandling(closeButtonRef);
    const overlayStacking = useOverlayStacking(undefined, 'feature-highlight');
    const ariaLabelingProps = _useAriaLabelingProps(remainingProps);
    const forwardProps = useSafeForwardProps(remainingProps, ariaLabelingProps);
    const intl = useIntl();
    const slots: FeatureHighlightSlots = {
      title: null,
      visual: null,
      content: null,
      actions: null,
    };

    // featureHighlight should be placed at the end of the DOM but before microguide and notifications
    useLayoutEffect(() => {
      const toastContainer = document.querySelector('[data-toast-container]');
      const microguideContainer = document.querySelector(
        '[data-microguide-container]',
      );
      featureHighlightContainer.current = document.createElement('div');
      featureHighlightContainer.current.setAttribute('aria-live', 'polite');
      featureHighlightContainer.current.setAttribute(
        'data-feature-highlight-container',
        'true',
      );

      if (microguideContainer && microguideContainer.parentNode) {
        microguideContainer.parentNode.insertBefore(
          featureHighlightContainer.current,
          microguideContainer,
        );
      } else if (toastContainer && toastContainer.parentNode) {
        toastContainer.parentNode.insertBefore(
          featureHighlightContainer.current,
          toastContainer,
        );
      } else {
        document.body.appendChild(featureHighlightContainer.current);
      }

      return () => {
        featureHighlightContainer.current?.remove();
      };
    }, []);

    Children.forEach(children, (child) => {
      if (isSlot(child, Title)) {
        slots.title = child;
      } else if (isSlot(child, Visual)) {
        slots.visual = child;
      } else if (isSlot(child, Content)) {
        slots.content = child;
      } else if (isSlot(child, Actions)) {
        slots.actions = child;
      } else if (isValidElement(child) && isDev()) {
        // eslint-disable-next-line no-console
        console.warn(
          'Only Title, Visual, Content and Actions are valid children of FeatureHighlight.',
        );
      } else if (isDev() && child !== null) {
        // eslint-disable-next-line no-console
        console.warn('The used child is not supported by FeatureHighlight.');
      }
    });

    if (slots.title && ariaLabelingProps['aria-labelledby'] === undefined) {
      ariaLabelingProps['aria-labelledby'] = 'strato-feature-highlight-title';
    }
    if (slots.content && ariaLabelingProps['aria-describedby'] === undefined) {
      ariaLabelingProps['aria-describedby'] =
        'strato-feature-highlight-content';
    }

    // Handle the escape key
    const closeOnEscape = (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closedOnKeydown.current = true;
        onClose();
      }
    };

    const offsetModifier = useMemo(
      () => ({
        name: 'offset',
        options: {
          offset: (({ placement: popperPlacement, popper }) => {
            let skidding = 0;
            const spacingToPopperEdge = 24;
            if (popperPlacement === preferredPlacement) {
              switch (placement) {
                case 'right-bottom':
                case 'left-bottom':
                  skidding = popper.height / 2 - spacingToPopperEdge;
                  break;
                case 'right-top':
                case 'left-top':
                  skidding = -(popper.height / 2 - spacingToPopperEdge);
                  break;
                case 'top-left':
                case 'bottom-left':
                  skidding = -(popper.width / 2 - spacingToPopperEdge);
                  break;
                case 'top-right':
                case 'bottom-right':
                  skidding = popper.width / 2 - spacingToPopperEdge;
                  break;
                default:
                  skidding = 0;
              }
            }
            return [skidding, 16];
          }) as OffsetsFunction,
        },
      }),
      [placement, preferredPlacement],
    );

    const { styles, attributes } = usePopper(anchor, overlayElement, {
      placement: preferredPlacement,
      modifiers: [
        offsetModifier,
        {
          name: 'flip',
          options: {
            fallbackPlacements: [...basePlacements],
          },
        },
        {
          name: 'arrow',
          options: {
            element: arrowElement,
            padding: 16,
          },
        },
        // TODO: Deprecation is only happening to the public export, the internal function will still be available
        // can be removed with APPDEV-12712
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        observeOverlaySize,
        observeAnimationDone,
        eventListeners,
      ],
    });

    useEffect(() => {
      if (open === false) {
        // handle focus if closed via keyboard
        handleFocusOnKeydown();
      }
    }, [handleFocusOnKeydown, open]);

    return open && anchor
      ? createPortal(
          <Flex
            ref={rootRef}
            onKeyDown={closeOnEscape}
            role="dialog"
            aria-modal="false"
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            style={{ ...styles.popper, zIndex: overlayStacking.style.zIndex }}
            {...attributes.popper}
            {...forwardProps}
            {...ariaLabelingProps}
          >
            <Flex
              className={FeatureHighlightScrollContainerCSS}
              flexDirection="column"
              gap={0}
              paddingX={16}
              paddingTop={8}
              paddingBottom={8}
            >
              <Flex
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={8}
                paddingBottom={
                  slots.content !== null ||
                  slots.actions !== null ||
                  slots.visual !== null
                    ? 8
                    : 0
                }
              >
                <Flex
                  flexItem
                  flexGrow={1}
                  paddingY={6}
                  id="strato-feature-highlight-title"
                >
                  {slots.title}
                </Flex>
                <Button
                  className={FeatureHighlightCloseButtonCSS}
                  onClick={() => onClose()}
                  onKeyDown={() => (closedOnKeydown.current = true)}
                  ref={closeButtonRef}
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Close feature highlight',
                    id: 'XQHEJyt2t/js0TGr',
                    description: 'Close the feature highlight.',
                  })}
                >
                  <Button.Prefix>
                    <XmarkIcon />
                  </Button.Prefix>
                </Button>
              </Flex>
              {slots.visual && (
                <Flex
                  paddingBottom={
                    slots.content !== null || slots.actions !== null ? 16 : 8
                  }
                >
                  {slots.visual}
                </Flex>
              )}
              {slots.content && (
                <Flex
                  paddingBottom={8}
                  style={{ overflow: 'hidden' }}
                  id="strato-feature-highlight-content"
                >
                  {slots.content}
                </Flex>
              )}
              {slots.actions}
            </Flex>
            <div
              data-popper-arrow
              ref={setArrowElement}
              className={FeatureHighlightPointerArrowCSS}
              style={styles.arrow}
              {...attributes.arrow}
            />
          </Flex>,
          featureHighlightContainer.current || document.body,
        )
      : null;
  },
);

/**
 * The `FeatureHighlight` supports the onboarding of new users, as well as the introduction of new features to existing users.
 * It is positioned next to the specified reference element and has four optional slots: `Title`, `Visual`, `Content` and `Actions`.
 * @public
 */
export const FeatureHighlight = Object.assign(FeatureHighlightRoot, {
  Title,
  Visual,
  Content,
  Actions,
});
